import { isFedRamp } from '@atlassian/atl-context';
import { Placement, usePostOfficeContext } from '@atlassian/post-office-frontend';
import RemoteSwitcherPlugin from '@atlassian/remote-module-loader/plugin-switcher';
import { RemoteComponent } from '@atlassian/remote-module-loader/remote-component';
import AtlassianSwitcher from '@atlassian/switcher';
import { observer } from 'mobx-react';
import React, { useMemo } from 'react';

import { getEnv } from '../config';
import { store } from '../singletons';
import { getPostOfficeEnv } from '../util/post-office';

function remoteSwitcherEnv() {
  if (isFedRamp()) {
    return getEnv().includes('prod') ? 'prod-fedm-east' : 'stg-fedm-east';
  }
  return getEnv().includes('prod') ? 'production' : 'staging';
}

const remoteSwitcherPlugin = new RemoteSwitcherPlugin({
  env: remoteSwitcherEnv(),
  variant: 'latest',
});

const standardizeLocale = (locale?: string): string | undefined => {
  return locale ? locale.replace('_', '-') : undefined;
};

type SwitcherComponentProps = React.ComponentProps<typeof AtlassianSwitcher>;

export type Cohort = 'not-enrolled' | 'control' | 'variation' | 'not-enrolled-but-visible';

const { fireEventOnce } = (() => {
  let hasFired = false;

  return {
    fireEventOnce: (fireFunction: () => void) => {
      if (!hasFired) {
        fireFunction();
        hasFired = true;
      }
    },
  };
})();

const Switcher = observer(() => {
  const isPostOfficeIntegrationEnabled = store.featureFlagsStore.flags.post_office_in_uchi_switcher;
  const postOfficeContext = usePostOfficeContext();

  const crossFlowProps = useMemo(
    () => ({
      isDiscoverMoreForEveryoneEnabled: true,
      discoverySection: isPostOfficeIntegrationEnabled ? (
        <Placement
          placementId="app-switcher-discovery-section"
          env={getPostOfficeEnv()}
          variant="latest"
          context={postOfficeContext}
        />
      ) : (
        undefined
      ),
    }),
    [],
  );

  const switcherProps = useMemo<SwitcherComponentProps>(() => {
    return {
      appearance: 'standalone',
      product: 'start',
      disableCustomLinks: true,
      ...crossFlowProps,
    };
  }, [crossFlowProps]);

  return (
    <RemoteComponent<SwitcherComponentProps>
      remotePlugin={remoteSwitcherPlugin}
      analyticsChannel="navigation"
      ErrorFallback={<AtlassianSwitcher {...switcherProps} />}
      // switcher props
      {...switcherProps}
    />
  );
});

export default Switcher;
